import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { api, setActiveWorkspaceHeader } from "@/lib/quotationApi";

const AuthContext = createContext(null);

export const ROLE_RANK = { staff: 1, manager: 2, admin: 3 };

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export function signInWithGoogle() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(
    redirectUrl
  )}`;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const applyMe = useCallback((me) => {
    setUser(me);
    setActiveWorkspaceHeader(me?.active_workspace_id || null);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      applyMe(r.data);
    } catch {
      setUser(null);
      setActiveWorkspaceHeader(null);
    } finally {
      setLoading(false);
    }
  }, [applyMe]);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const switchWorkspace = useCallback(
    async (wsId) => {
      setActiveWorkspaceHeader(wsId);
      await api.post("/workspaces/switch", { workspace_id: wsId });
      await checkAuth();
    },
    [checkAuth]
  );

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      console.warn("Logout request failed:", e);
    }
    setUser(null);
    setActiveWorkspaceHeader(null);
    window.location.href = "/";
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      role: user?.role || null,
      capabilities: user?.capabilities || {},
      can: (cap) => Boolean(user?.capabilities?.[cap]),
      refresh: checkAuth,
      switchWorkspace,
      logout,
      signIn: signInWithGoogle,
    }),
    [user, loading, checkAuth, switchWorkspace, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export function hasRole(role, minRole) {
  return (ROLE_RANK[role] || 0) >= (ROLE_RANK[minRole] || 0);
}
